import { useState } from "react";
import { Sun, Moon, LogOut, BarChart3, Calendar, ClipboardList, Users, Clock, Menu, X, MessageSquare, Megaphone, Settings, GraduationCap, Scale, Briefcase, BookOpen } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { RoadmapProvider, useRoadmap } from "@/context/RoadmapContext";
import CalendarView from "@/components/roadmap/CalendarView";
import VueStrategique from "@/components/roadmap/VueStrategique";
import RecueilOperationnel from "@/components/roadmap/RecueilOperationnel";
import StrategieRecrutement from "@/components/roadmap/StrategieRecrutement";
import HistoriqueView from "@/components/roadmap/HistoriqueView";
import ConsignesView from "@/components/roadmap/ConsignesView";
import EmptyTab from "@/components/roadmap/EmptyTab";
import "@/styles/roadmap.css";

const TABS = [
  { id: "strategy", label: "Vue Stratégique", icon: BarChart3, tabName: "Vue Stratégique", totalTasks: 29 },
  { id: "recueil", label: "Recueil Opérationnel", icon: ClipboardList, tabName: "Recueil Opérationnel", totalTasks: 114 },
  { id: "optimisation", label: "Optimisation des processus", icon: Settings, tabName: null, totalTasks: 0 },
  { id: "juridique", label: "Juridique", icon: Scale, tabName: null, totalTasks: 0 },
  { id: "calendar", label: "Calendrier", icon: Calendar, tabName: null, totalTasks: 0 },
  { id: "consignes", label: "Consignes", icon: BookOpen, tabName: null, totalTasks: 0 },
  { id: "separator-core-1", type: "separator" },
  { id: "recrutement", label: "Recrutement", icon: Users, tabName: "Stratégie Recrutement", totalTasks: 11 },
  { id: "formation", label: "Formation", icon: GraduationCap, tabName: null, totalTasks: 0 },
  { id: "separator-core-2", type: "separator" },
  { id: "communication", label: "Communication", icon: MessageSquare, tabName: null, totalTasks: 0 },
  { id: "publicite", label: "Publicité", icon: Megaphone, tabName: null, totalTasks: 0 },
  { id: "commercial", label: "Commercial", icon: Briefcase, tabName: null, totalTasks: 0 },
  { id: "separator-core-3", type: "separator" },
  { id: "historique", label: "Historique", icon: Clock, tabName: null, totalTasks: 0 },
];

function ProgressRing({ done, total, size = 28 }) {
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const r = (size - 4) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (pct / 100) * c;
  const color = pct === 100 ? "stroke-green-500" : pct > 0 ? "stroke-[#4c7e55] dark:stroke-[#2e6d3b]" : "stroke-[#c9d8cb] dark:stroke-[#35513a]";

  return (
    <div className="relative flex items-center justify-center shrink-0" style={{ width: size, height: size }} data-testid="progress-ring">
      <svg width={size} height={size} className="transform -rotate-90 absolute inset-0">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" strokeWidth={2.5} className="stroke-[#dfe9e0] dark:stroke-[#2c382e]" />
        {pct > 0 && (
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" strokeWidth={2.5}
            className={`${color} transition-all duration-700 ease-out`}
            strokeDasharray={c} strokeDashoffset={offset} strokeLinecap="round" />
        )}
      </svg>
      <span className={`text-[8px] font-black ${pct > 0 ? "text-gray-700 dark:text-gray-200" : "text-[#8fa191] dark:text-[#64806a]"}`}>{pct}</span>
    </div>
  );
}

function RoadmapLayout() {
  const { logout } = useAuth();
  const { theme, toggleTheme, tabStats } = useRoadmap();
  const [activeTab, setActiveTab] = useState("strategy");
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const activeLabel = TABS.find(t => t.id === activeTab)?.label || "";

  return (
    <div className="roadmap-shell h-screen flex overflow-hidden" data-testid="roadmap-page-container">
      {/* MOBILE OVERLAY */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/40 z-30 md:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* SIDEBAR — full height */}
      <aside
        className={`sidebar-nav shrink-0 bg-white dark:bg-[#1a1a1a] border-r border-gray-200 dark:border-gray-800 flex flex-col z-40
          fixed md:relative inset-y-0 left-0 md:translate-x-0 transition-transform duration-200 ease-out
          ${sidebarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}
          w-[17.5rem] lg:w-[18.75rem]`}
        data-testid="sidebar"
      >
        {/* LOGO */}
        <div className="px-4 pt-4 pb-3 border-b border-gray-200 dark:border-gray-800 shrink-0 flex justify-center">
          <img
            src={theme === "dark" ? "/logo-braux-white.png" : "/logo-braux-dark.png"}
            alt="Braux"
            className="h-10 w-auto"
            data-testid="sidebar-logo"
          />
        </div>

        {/* TABS */}
        <nav className="flex-1 py-3 px-2 space-y-0.5 overflow-y-auto" data-testid="tab-bar">
          {TABS.map((tab) => {
            if (tab.type === "separator") {
              return (
                <div
                  key={tab.id}
                  className="mx-3 my-2 h-px bg-[#eef3ee] dark:bg-[#202821]"
                  data-testid={tab.id}
                />
              );
            }

            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            const total = tab.totalTasks;
            const done = tab.tabName && tabStats[tab.tabName] ? tabStats[tab.tabName].done : 0;
            const hasProgress = total > 0;

            return (
              <button
                key={tab.id}
                onClick={() => { setActiveTab(tab.id); setSidebarOpen(false); }}
                className={`sidebar-tab w-full flex items-center gap-3 px-3 py-2 rounded-lg text-[13px] font-semibold transition-all ${
                  isActive
                    ? "bg-[#eef4ef] dark:bg-[rgba(46,109,59,0.24)] text-[#4c7e55] dark:text-[#2e6d3b]"
                    : "text-gray-600 dark:text-gray-400 hover:bg-[#f4f7f4] dark:hover:bg-[#202821] hover:text-gray-900 dark:hover:text-white"
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <Icon className={`w-4 h-4 shrink-0 ${isActive ? "text-[#4c7e55] dark:text-[#2e6d3b]" : ""}`} />
                <span className="truncate flex-1 text-left">{tab.label}</span>
                {hasProgress && <ProgressRing done={done} total={total} />}
                {!hasProgress && isActive && <div className="w-1.5 h-1.5 rounded-full bg-[#4c7e55] dark:bg-[#2e6d3b] shrink-0" />}
              </button>
            );
          })}
        </nav>

        {/* BRANDING */}
        <div className="px-4 py-3 text-center border-t border-gray-200 dark:border-gray-800" data-testid="sidebar-branding">
          <span className="font-['Playfair_Display'] text-[1.5rem] leading-tight text-black dark:text-white">
            Administratif<br />Finance &amp;<br />Strat&eacute;gie
          </span>
        </div>

        {/* BOTTOM ACTIONS */}
        <div className="p-3 border-t border-gray-200 dark:border-gray-800 flex items-center justify-center gap-1.5">
          <button onClick={toggleTheme} className="p-1.5 rounded-md bg-[#f2f6f2] dark:bg-[#202821] text-[#6b7d6e] dark:text-[#8ea391] hover:bg-[#e5ede6] dark:hover:bg-[#2a342c] transition-colors" data-testid="theme-toggle-btn">
            {theme === "dark" ? <Sun className="w-3.5 h-3.5" /> : <Moon className="w-3.5 h-3.5" />}
          </button>
          <button onClick={logout} className="p-1.5 rounded-md bg-[#f2f6f2] dark:bg-[#202821] text-[#6b7d6e] dark:text-[#8ea391] hover:bg-red-100 dark:hover:bg-red-900/30 hover:text-red-600 dark:hover:text-red-400 transition-colors" data-testid="logout-btn">
            <LogOut className="w-3.5 h-3.5" />
          </button>
        </div>
      </aside>

      {/* MAIN CONTENT */}
      <main className="flex-1 flex flex-col overflow-hidden" data-testid="roadmap-main">
        {/* Mobile top bar */}
        <div className="md:hidden flex items-center gap-3 px-3 py-2 bg-white dark:bg-[#1a1a1a] border-b border-gray-200 dark:border-gray-800 shrink-0">
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2 rounded-lg bg-[#f2f6f2] dark:bg-[#202821] text-gray-600 dark:text-gray-300" data-testid="sidebar-toggle">
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
          <span className="text-sm font-bold text-gray-900 dark:text-white truncate">{activeLabel}</span>
        </div>

        {/* Content area */}
        <div className={`flex-1 ${activeTab === "calendar" ? "overflow-hidden" : "overflow-y-auto scroll-smooth px-3 sm:px-6 py-4 sm:py-6"}`}>
          <div className={activeTab === "calendar" ? "h-full flex flex-col" : "max-w-6xl 2xl:max-w-[90rem] mx-auto w-full"}>
            {activeTab === "strategy" && <VueStrategique />}
            {activeTab === "consignes" && <ConsignesView />}
            {activeTab === "calendar" && <CalendarView />}
            {activeTab === "recueil" && <RecueilOperationnel />}
            {activeTab === "recrutement" && <StrategieRecrutement />}
            {activeTab === "historique" && <HistoriqueView />}
            {activeTab === "communication" && <EmptyTab title="Communication" />}
            {activeTab === "publicite" && <EmptyTab title={"Publicit\u00e9"} />}
            {activeTab === "optimisation" && <EmptyTab title={"Optimisation des processus"} />}
            {activeTab === "formation" && <EmptyTab title="Formation" />}
            {activeTab === "juridique" && <EmptyTab title="Juridique" />}
            {activeTab === "commercial" && <EmptyTab title="Commercial" />}
          </div>
        </div>
      </main>
    </div>
  );
}

export default function RoadmapPage() {
  return (
    <RoadmapProvider>
      <RoadmapLayout />
    </RoadmapProvider>
  );
}
